/**
 * Apresentação dos agendamentos de mensagem: status real do envio (Pending/Processing/Completed/Failed
 * do SchedulerWorker) e datas em português, sem depender do locale do dayjs.
 */

export type ScheduleTone = 'scheduled' | 'overdue' | 'sending' | 'sent' | 'failed';

export interface ScheduleStatusInfo {
  label: string;
  tone: ScheduleTone;
}

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export function scheduleStatus(status: string | null | undefined, whenMs: number | null, nowMs: number): ScheduleStatusInfo {
  const s = (status || '').trim().toLowerCase();
  if (s === 'completed') return { label: 'Enviado', tone: 'sent' };
  if (s === 'failed') return { label: 'Falhou', tone: 'failed' };
  if (s === 'processing') return { label: 'Enviando', tone: 'sending' };
  // Pending: no futuro é agendado; com a hora passada ainda não saiu (fila atrasada ou recorrência pendente).
  if (whenMs != null && whenMs < nowMs) return { label: 'Pendente', tone: 'overdue' };
  return { label: 'Agendado', tone: 'scheduled' };
}

/**
 * ScheduledTime do backend é instante UTC; sem "Z"/offset (DateTime Unspecified do Npgsql) o navegador
 * o leria como hora local e deslocaria o horário pelo fuso do usuário.
 */
export function utcInstantMs(raw: string | null | undefined): number | null {
  const s = (raw || '').trim();
  if (!s) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const ms = Date.parse(hasZone ? s : `${s}Z`);
  return Number.isFinite(ms) ? ms : null;
}

/** "em 3 h", "amanhã", "em 4 dias", "há 2 dias", "agora". */
export function relativeWhen(whenMs: number | null, nowMs: number): string {
  if (whenMs == null) return '';
  const diffMin = Math.round((whenMs - nowMs) / 60_000);
  const abs = Math.abs(diffMin);
  if (abs < 1) return 'agora';
  const past = diffMin < 0;
  if (abs < 60) return past ? `há ${abs} min` : `em ${abs} min`;
  const hours = Math.round(abs / 60);
  if (hours < 24) return past ? `há ${hours} h` : `em ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return past ? 'ontem' : 'amanhã';
  return past ? `há ${days} dias` : `em ${days} dias`;
}

/** Partes da data no fuso já aplicado pelo chamador (Date local do valor convertido). */
export function dateParts(d: Date): { day: string; month: string; weekday: string; time: string } {
  return {
    day: String(d.getDate()).padStart(2, '0'),
    month: MONTHS[d.getMonth()],
    weekday: WEEKDAYS[d.getDay()],
    time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
  };
}

/** "1 contato" / "20 contatos" a partir do texto "N recipient(s)" ou de um número. */
export function recipientsLabel(raw: string | number | null | undefined): string {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? '').replace(/\D+/g, ' ').trim().split(' ')[0] || '', 10);
  if (!Number.isFinite(n)) return String(raw ?? '');
  return n === 1 ? '1 destinatário' : `${n} destinatários`;
}
